import {
  Button,
  Card,
  DatePickerDropdown,
  HSpacer,
  Text,
  TextLink,
  VSpacer,
} from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import {
  FriendlySyncStatus,
} from '@/pages/Admin/HierarchyOfRetailers/Integrations/FriendlySyncStatus';
import { FriendlySyncType } from '@/pages/Admin/HierarchyOfRetailers/Integrations/FriendlySyncType';
import {
  SaveIntegrationDetailsModal,
} from '@/pages/Admin/HierarchyOfRetailers/Integrations/SaveIntegrationDetailsModal';
import { SyncHistoryModal } from "@/pages/Admin/HierarchyOfRetailers/Integrations/SyncHistoryModal";
import {
  TransactionSyncConfirmModal,
} from '@/pages/Admin/HierarchyOfRetailers/Integrations/TransactionSyncConfirmModal';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { IntegrationApi } from '@/utilities/api/IntegrationApi';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { ApiIntegrationSyncLog } from "@api/interfaces/ApiIntegrationSyncLog";
import Autorenew from '@mui/icons-material/Autorenew';
import AddIcon from '@mui/icons-material/Add';
import {
  Box, Stack, Table, TableCell, TableHead, TableRow, useTheme,
} from '@mui/material';
import { ApiErrors, IntegrationSyncStatus, IntegrationSyncType } from '@shared/enums';
import { formatDateTime, omit } from '@shared/utilities';
import { stringify } from 'csv-stringify/browser/esm/sync';
import i18next from 'i18next';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

interface IntegrationDetailsProps {
  isJobProcessing: boolean,
  retailerId: string,
}

const OrderedSyncList = [
  IntegrationSyncType.Locations,
  IntegrationSyncType.Salespeople,
  IntegrationSyncType.Customers,
  IntegrationSyncType.Products,
  IntegrationSyncType.Transactions,
];

export const handleDownloadErrorCsv = (
  syncType: IntegrationSyncType,
  logs: ApiIntegrationSyncLog[],
) => {
  const csvString = stringify(
    logs.map((log) => omit(log, ['createdAt', 'id', 'integrationSyncId', 'updatedAt'])),
    { header: true },
  );

  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    // Browsers that support HTML5 download attribute
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${syncType}-error-logs.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const IntegrationDetails = ({
  isJobProcessing,
  retailerId,
}: IntegrationDetailsProps) => {
  const [showIntegrationDetailsModal, setShowIntegrationDetailsModal] = useState(false);
  const [showTransactionSyncConfirmModal, setShowTransactionSyncConfirmModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(undefined);
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(undefined);
  const [isCustomStartDateValid, setIsCustomStartDateValid] = useState(true);
  const [isCustomEndDateValid, setIsCustomEndDateValid] = useState(true);
  const [isSyncingTransactions, setIsSyncingTransactions] = useState(false);

  const [showSyncHistoryModal, setShowSyncHistoryModal] = useState(false);
  const [selectedSyncHistorySyncType, setSelectedSyncHistorySyncType] =
    useState<IntegrationSyncType | null>(null);

  const [syncTypeSyncSelected, setSyncTypeSyncSelected] = useState<IntegrationSyncType>();

  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const theme = useTheme();

  const { data: existingIntegration } = useQuery(
    [QueryKeys.GET_RETAILER_INTEGRATION, retailerId],
    async () => IntegrationApi.getByRetailerId(retailerId),
    {
      refetchInterval: () => isJobProcessing ? 5000 : false,
    },
  );

  const { data: earliestResponse } = useQuery(
    [QueryKeys.GET_EARLIEST_SYNC_TRANSACTION_DATE, retailerId],
    async () => RetailerApi.getEarliestSyncedTransactionDate(retailerId),
  );

  const { data: syncStatus } = useQuery(
    [QueryKeys.GET_INTEGRATION_SYNC_STATUS, retailerId],
    async () => IntegrationApi.getSyncStatus(retailerId),
    { enabled: !isEmpty(existingIntegration), refetchInterval: 5000 },
  );

  const { data: categories } = useQuery(
    [QueryKeys.GET_RETAILER_CATEGORIES, retailerId],
    async () => await RetailerApi.listCategories(retailerId),
  );

  useEffect(() => {
    void queryClient.invalidateQueries(QueryKeys.GET_INTEGRATION_SYNC_STATUS);
    void queryClient.invalidateQueries(QueryKeys.GET_RETAILER_INTEGRATION);
  }, [isJobProcessing, queryClient]);

  useEffect(() => {
    if (syncStatus) {
      const successStatuses: IntegrationSyncStatus[] = [
        IntegrationSyncStatus.Success,
        IntegrationSyncStatus.SuccessWithWarnings,
      ];
      if (
        syncStatus[IntegrationSyncType.Transactions].syncStatus === IntegrationSyncStatus.Syncing
      ) {
        setIsSyncingTransactions(true);
      } else if (
        syncStatus[IntegrationSyncType.Transactions].syncStatus
        && successStatuses.includes(syncStatus[IntegrationSyncType.Transactions].syncStatus)
        && isSyncingTransactions
      ) {
        void queryClient.invalidateQueries(QueryKeys.GET_EARLIEST_SYNC_TRANSACTION_DATE);
        setIsSyncingTransactions(false);
      }
    }
  }, [syncStatus]);

  const getSyncStatusText = (type: IntegrationSyncType) => {
    if (!syncStatus) {
      return '';
    }
    if (type === IntegrationSyncType.Transactions) {
      if (syncStatus[type].hasFailedProcessing) {
        return 'Processing failed';
      }
      if (syncStatus[type].isProcessing) {
        return 'Rewards processing';
      }
    }
    if (syncStatus[type].syncStatus) {
      return FriendlySyncStatus[syncStatus[type].syncStatus!];
    }
    return 'Not started';
  };

  const isSyncButtonDisabled = (type: IntegrationSyncType) => {
    const isSyncing = syncStatus?.[type].syncStatus === IntegrationSyncStatus.Syncing
      || syncStatus?.[type].syncStatus === IntegrationSyncStatus.Pending;
    if (!existingIntegration || isSyncing || !!syncTypeSyncSelected) {
      return true;
    }
    const {
      hasSyncedCustomers,
      hasSyncedLocations,
      hasSyncedProducts,
      hasSyncedSalespeople,
    } = existingIntegration;
    if (type === IntegrationSyncType.Customers && (!hasSyncedSalespeople || !hasSyncedLocations)) {
      return true;
    } else if (type === IntegrationSyncType.Products && !hasSyncedLocations) {
      return true;
    } else if (type === IntegrationSyncType.Products && !categories?.length) {
      return true;
    } else if (type === IntegrationSyncType.Transactions) {
      if (!(hasSyncedCustomers
        && hasSyncedLocations
        && hasSyncedProducts
        && hasSyncedSalespeople
      )) {
        return true;
      }
      if (customStartDate && customEndDate && customStartDate > customEndDate) {
        return true;
      }
      if (!isCustomStartDateValid || !isCustomEndDateValid) {
        return true;
      }
    }
    return false;
  };

  const showSyncButton = (type: IntegrationSyncType) => {
    const successfulStatuses = [
      IntegrationSyncStatus.Success,
      IntegrationSyncStatus.SuccessWithWarnings,
    ];

    if (!syncStatus || !syncStatus[type] || !syncStatus[type].syncStatus) {
      return true;
    }
    if (type === IntegrationSyncType.Transactions && customStartDate) {
      return true;
    }
    return (!successfulStatuses.includes(syncStatus[type].syncStatus as IntegrationSyncStatus));
  };

  const syncTransactions = async () => {
    await IntegrationApi.sync(retailerId, {
      endDate: customEndDate
        ? DateTime.fromJSDate(customEndDate).toFormat('yyyy-MM-dd')
        : undefined,
      type: IntegrationSyncType.Transactions,
      startDate: customStartDate
        ? DateTime.fromJSDate(customStartDate).toFormat('yyyy-MM-dd')
        : undefined,
    });
    void queryClient.invalidateQueries(QueryKeys.GET_INTEGRATION_SYNC_STATUS);
    void queryClient.invalidateQueries(QueryKeys.GET_JOB_IS_PROCESSING);
  };

  const handleSyncButtonClick = async (type: IntegrationSyncType) => {
    if (type === IntegrationSyncType.Transactions) {
      const hasSync = syncStatus?.[IntegrationSyncType.Transactions]
        ?.lastSuccessfulSyncDate;
      if (hasSync) {
        await syncTransactions();
      } else {
        setShowTransactionSyncConfirmModal(true);
      }
    } else {
      setSyncTypeSyncSelected(type);
      await IntegrationApi.sync(retailerId, { type });
      setTimeout(() => {
        void queryClient.invalidateQueries(
          QueryKeys.GET_INTEGRATION_SYNC_STATUS,
        );
        void queryClient.invalidateQueries(QueryKeys.GET_JOB_IS_PROCESSING);
        setSyncTypeSyncSelected(undefined);
      }, 1000);
    }
  };

  const handleRunPointsEngineClick = async () => {
    try {
      await RetailerApi.processRewards(retailerId);
      void queryClient.invalidateQueries(QueryKeys.GET_INTEGRATION_SYNC_STATUS);
      void queryClient.invalidateQueries(
        QueryKeys.GET_IMPORT_PROCESSING_RESULT,
      );
      void queryClient.invalidateQueries(QueryKeys.GET_JOB_IS_PROCESSING);
    } catch (e: any) {
      if (e.message === ApiErrors.pointsEngineAlreadyRunning) {
        openSnackbar(e.message);
        void queryClient.invalidateQueries(QueryKeys.GET_JOB_IS_PROCESSING);
      } else {
        openSnackbar('Rewards processing failed');
      }
    }
  };

  const showRunRewardsButton = (
    type: IntegrationSyncType,
    isProcessing: boolean,
    hasFailedProcessing: boolean,
  ) => {
    return !showSyncButton(type)
      && type === IntegrationSyncType.Transactions
      && (hasFailedProcessing || isProcessing);
  };

  return (
    <Stack spacing={2}>
      <Card elevation={0} testID="integration-details-card">
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Stack>
            <Text category="title-small">Integration Details</Text>
            <VSpacer size="2" />
            <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
              Connection
            </Text>
          </Stack>
          <Button
            onClick={() => setShowIntegrationDetailsModal(true)}
            startIcon={isEmpty(existingIntegration) ? <AddIcon /> : null }
            testID="show-integration-details-button"
          >
            {isEmpty(existingIntegration) ? 'Add' : 'View'}
          </Button>
          {showIntegrationDetailsModal && (
            <SaveIntegrationDetailsModal
              existingIntegration={isEmpty(existingIntegration) ? null : existingIntegration}
              onClose={() => setShowIntegrationDetailsModal(false)}
              onSave={() => queryClient.invalidateQueries(QueryKeys.GET_RETAILER_INTEGRATION)}
              retailerId={retailerId}
            />
          )}
        </Stack>
      </Card>
      {!isEmpty(existingIntegration) && (
        <Card
          cardContentStyle={{ '&:last-child': { paddingBottom: 0 }, padding: 0 }}
          elevation={0}
          testID="data-syncs-card"
        >
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
            sx={{ borderBottom: `1px solid ${theme.palette.divider}`, p: '16px 20px' }}
          >
            <Text category="title-small">Data Syncs</Text>
            <Stack alignItems="center" direction="row">
              <Text sx={{ color: theme.palette.text.secondary }}>
                Earliest synced transaction date:
              </Text>
              <HSpacer size="2" />
              <Text fontWeight={500}>
                {earliestResponse?.earliestTransactionDate
                  ? DateTime.fromJSDate(
                    new Date(earliestResponse.earliestTransactionDate),
                  ).toFormat('MM/dd/yyyy')
                  : 'N/A'
                }
              </Text>
            </Stack>
          </Stack>
          <Table sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell width={150}>Type</TableCell>
                <TableCell width={150}>Status</TableCell>
                <TableCell width={150}>Last successful sync</TableCell>
                <TableCell width={150}>Action</TableCell>
                <TableCell width={150}>Errors</TableCell>
                <TableCell align="right" width={150}>Sync history</TableCell>
              </TableRow>
            </TableHead>
            {OrderedSyncList.map((type) => (
              <TableRow key={type}>
                <TableCell>{FriendlySyncType[type]}</TableCell>
                <TableCell>
                  {getSyncStatusText(type)}
                </TableCell>
                <TableCell>
                  {syncStatus?.[type]?.lastSuccessfulSyncDate
                    && formatDateTime(syncStatus?.[type].lastSuccessfulSyncDate, i18next)}
                </TableCell>
                <TableCell>
                    <Button
                      disabled={isSyncButtonDisabled(type)}
                      loading={syncTypeSyncSelected === type
                        || syncStatus?.[type]?.syncStatus === IntegrationSyncStatus.Syncing
                        || syncStatus?.[type]?.syncStatus === IntegrationSyncStatus.Pending
                      }
                      onClick={() => handleSyncButtonClick(type)}
                      testID={`sync-${type}-button`}
                      variant="outlined"
                    >
                      Sync
                    </Button>
                  {showRunRewardsButton(
                    type,
                    syncStatus?.[type]?.isProcessing ?? false,
                    syncStatus?.[type]?.hasFailedProcessing ?? false,
                  ) &&
                    <Button
                      color="primary"
                      loading={syncStatus?.[type]?.isProcessing ?? false}
                      onClick={handleRunPointsEngineClick}
                      startIcon={<Autorenew />}
                      testID="points-engine-button"
                      variant="outlined"
                    >
                      Run points engine
                    </Button>
                  }
                </TableCell>
                <TableCell>
                  {!!syncStatus?.[type]?.syncErrors.length && (
                    <Button
                      onClick={() => (
                        handleDownloadErrorCsv(type, syncStatus?.[type]?.syncErrors ?? [])
                      )}
                      testID={`download-${type}-errors-button`}
                      variant="outlined"
                    >
                      Download errors
                    </Button>
                  )}
                </TableCell>
                <TableCell align="right">
                  <TextLink
                    onClick={() => {
                      setSelectedSyncHistorySyncType(type);
                      setShowSyncHistoryModal(true);
                    }}
                    testID={`show-${type}-sync-history-button`}
                  >
                    View history
                  </TextLink>
                </TableCell>
              </TableRow>
            ))}
          </Table>
          <Stack direction="row" sx={{ p: '16px 20px' }}>
            <Box sx={{ flexShrink: 0, width: '450px' }} />
            <Stack direction="column">
              <DatePickerDropdown
                label="Custom sync transaction start date"
                onChange={setCustomStartDate}
                onValidityChange={setIsCustomStartDateValid}
                testID="customer-start-date-picker-dropdown"
                value={customStartDate}
                width={325}
              />
              <VSpacer size="2" />
              <DatePickerDropdown
                disabled={!customStartDate}
                errorMessage={
                  !!customEndDate && !!customStartDate && customStartDate > customEndDate
                    ? 'End Date can not be before Start Date' : undefined
                }
                label="Custom sync transaction end date"
                onChange={setCustomEndDate}
                onValidityChange={setIsCustomEndDateValid}
                testID="customer-end-date-picker-dropdown"
                value={customEndDate}
                width={325}
              />
            </Stack>
          </Stack>
        </Card>
      )}
      {showTransactionSyncConfirmModal && (
        <TransactionSyncConfirmModal
          onCancel={() => setShowTransactionSyncConfirmModal(false)}
          onConfirm={async () => {
            setShowTransactionSyncConfirmModal(false);
            await syncTransactions();
          }}
        />
      )}
      {showSyncHistoryModal && selectedSyncHistorySyncType && (
        <SyncHistoryModal
          onCancel={() => setShowSyncHistoryModal(false)}
          retailerId={retailerId}
          selectedSyncType={selectedSyncHistorySyncType}
        />
      )}
    </Stack>
  );
};
